// src/pages/evcc/EvccCmsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "../../api/axiosClient"; // auth-enabled axios client

import LoadingComponent from "../../components/common/Loading";
import HTMLParser from 'html-react-parser';

/** Allowed EVCC slugs → CMS key fragments
 *  (updated: removed logos/leaflets/trustmark; added trusted-partners & benefits)
 */
const SLUGS = {
    overview: "overview",
    "guidance-documents": "guidance-documents",
    "model-documents": "model-documents",
    "marketing-tools": "marketing-tools",
    "trusted-partners": "trusted-partners",
    benefits: "benefits",
};

// ---- helpers: axios fetch + HTML decoder/sanitizer
async function fetchCmsByPrefix(prefix, locale = "") {
    const params = { prefix };
    if (locale) params.locale = locale;
    const res = await api.get("/cms", { params });
    return res.data || {};
}

function decodeEntities(str) {
    if (typeof str !== "string") return "";
    const ta = document.createElement("textarea");
    ta.innerHTML = str;
    return ta.value;
}

function normalizeCmsHtml(val) {
    const s = typeof val === "string" ? val : "";
    const looksEncoded = /&lt;|&gt;|&amp;|&quot;|&#39;|&#x[0-9A-Fa-f]+;/.test(s);
    const decoded = looksEncoded ? decodeEntities(s) : s;
    return DOMPurify.sanitize(decoded);
}

// Read a CMS field (string or { value })
function readCmsText(cms, key, fallback = "") {
    const entry = cms?.[key];
    if (entry == null) return fallback;
    if (typeof entry === "string") return entry;
    if (typeof entry === "object" && "value" in entry) return entry.value ?? fallback;
    return fallback;
}

export default function EvccCmsPage() {
    const { slug = "" } = useParams();
    const { pathname } = useLocation();

    // Resolve to a valid slug without early returns (avoid conditional hooks)
    const validSlug = SLUGS[slug] ? slug : "overview";
    const cmsKey = SLUGS[validSlug];
    const cmsPrefix = `evcc.${cmsKey}.`; // e.g., "evcc.trusted-partners."

    // Build canonical redirect base
    const inCompany = pathname.startsWith("/company/");
    const inMember = pathname.startsWith("/member/");
    const baseRoot = inCompany ? "/company/evcc" : inMember ? "/member/evcc" : "/company/evcc";
    const needRedirect = slug !== validSlug;

    // Membership flags
    let company = null;
    try {
        company = JSON.parse(localStorage.getItem("company") || "null");
    } catch { }
    const isEvccMember = 
        (company?.is_evcc_member ?? null) !== null
            ? !!company?.is_evcc_member
            : localStorage.getItem("is_evcc_member") === "true";

    const [loading, setLoading] = useState(true);
    const [cms, setCms] = useState({});
    const [error, setError] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError("");

            // Gate: if not an EVCC member, don't fetch
            if (!isEvccMember) {
                if (alive) {
                    setCms({});
                    setLoading(false);
                }
                return;
            }

            try {
                const locale = ""; // add locale switching later if needed
                const data = await fetchCmsByPrefix(cmsPrefix, locale);

                if (alive) setCms(data || {});
            } catch (e) {
                if (alive) setError(e?.response?.data?.message || "Failed to load content.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [cmsPrefix, isEvccMember]);

    useEffect(() => {
        let t = readCmsText(cms, `${cmsPrefix}body`, "");

        if (!t || !t.length || t.length <= 0)
            return;

        var sections = document.querySelectorAll(".cms-section.collapsible");
        if (sections.length <= 0)
            return;

        for (let jj = 0; jj < sections.length; jj++) {
            let section = sections[jj];

            let button = document.createElement('div');
            button.className = 'minmaxbtn';

            button.addEventListener('click', (e) => {
                console.log("click", e);
                let parent = e.target.closest('.collapsible');
                if (parent)
                    parent.classList.toggle('closed');
            });

            section.appendChild(button);
        }
    }, [cms])

    // Canonicalize unknown slug to /overview
    if (needRedirect) {
        return <Navigate to={`${baseRoot}/${validSlug}`} replace />;
    }

    const fallbackTitle =
        {
            overview: "EVCC: Overview",
            "guidance-documents": "EVCC: Guidance Documents",
            "model-documents": "EVCC: Model Documents",
            "marketing-tools": "EVCC: Marketing Tools",
            "trusted-partners": "EVCC: Trusted Partners",
            benefits: "EVCC: Member Benefits",
        }[cmsKey] || "EVCC";

    const title = readCmsText(cms, `${cmsPrefix}title`, fallbackTitle);
    //const bodyHtml = normalizeCmsHtml(readCmsText(cms, `${cmsPrefix}body`, ""));
    const bodyHtml = readCmsText(cms, `${cmsPrefix}body`, "");

    if (loading)
        return (
            <>
                <LoadingComponent></LoadingComponent>
            </>
        )

    return (
        <div className="cms-page evcc">
            <div className="cms-header">
                <div className="cms-title">{title}</div>
            </div>

            <div className="cms-body">

                {!isEvccMember ?
                    <>
                        <div className="cms-section">
                            <div className="cms-title">
                                <div className="cms-icon">
                                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect width="36" height="36" rx="8" fill="#005695" />
                                        <g clip-path="url(#clip0_2069_2210)">
                                            <path d="M22.1667 8C21.9457 8 21.7337 8.0878 21.5774 8.24408C21.4211 8.40036 21.3333 8.61232 21.3333 8.83333C21.3333 11.2908 19.1808 12.1667 17.1667 12.1667H11.3333C10.4493 12.1667 9.60143 12.5179 8.97631 13.143C8.35119 13.7681 8 14.6159 8 15.5V17.1667C8.00182 17.6656 8.11622 18.1577 8.33467 18.6063C8.55312 19.0549 8.86999 19.4484 9.26167 19.7575L12.2383 26.4008C12.45 26.8767 12.7951 27.2811 13.2319 27.5649C13.6686 27.8487 14.1783 27.9998 14.6992 28C15.1136 27.9997 15.5214 27.8958 15.8854 27.6977C16.2494 27.4996 16.5581 27.2137 16.7834 26.8658C17.0086 26.518 17.1433 26.1193 17.1752 25.7061C17.2071 25.2929 17.1351 24.8783 16.9658 24.5L15.1325 20.5H17.1667C19.1808 20.5 21.3333 21.3758 21.3333 23.8333C21.3333 24.0543 21.4211 24.2663 21.5774 24.4226C21.7337 24.5789 21.9457 24.6667 22.1667 24.6667C22.3877 24.6667 22.5996 24.5789 22.7559 24.4226C22.9122 24.2663 23 24.0543 23 23.8333V8.83333C23 8.61232 22.9122 8.40036 22.7559 8.24408C22.5996 8.0878 22.3877 8 22.1667 8ZM15.4475 25.1825C15.5027 25.3072 15.5259 25.4436 15.515 25.5795C15.5042 25.7154 15.4595 25.8464 15.3852 25.9607C15.3109 26.075 15.2092 26.169 15.0894 26.234C14.9696 26.2991 14.8355 26.3332 14.6992 26.3333C14.5003 26.3332 14.3058 26.2753 14.1392 26.1667C13.9727 26.0582 13.8412 25.9035 13.7608 25.7217L11.4208 20.5H13.2992L15.4475 25.1825ZM21.3333 20.1667C20.1427 19.2493 18.6686 18.7776 17.1667 18.8333H11.3333C10.8913 18.8333 10.4674 18.6577 10.1548 18.3452C9.84226 18.0326 9.66667 17.6087 9.66667 17.1667V15.5C9.66667 15.058 9.84226 14.634 10.1548 14.3215C10.4674 14.0089 10.8913 13.8333 11.3333 13.8333H17.1667C18.6681 13.8902 20.1422 13.42 21.3333 12.5042V20.1667ZM27.9167 20.8767C27.8678 20.9746 27.8001 21.062 27.7174 21.1338C27.6347 21.2056 27.5387 21.2604 27.4349 21.2951C27.331 21.3298 27.2213 21.3436 27.1121 21.3359C27.0029 21.3281 26.8963 21.299 26.7983 21.25L25.1317 20.4167C24.9339 20.3179 24.7834 20.1445 24.7134 19.9348C24.6434 19.7251 24.6595 19.4961 24.7583 19.2983C24.8571 19.1005 25.0305 18.9501 25.2402 18.8801C25.4499 18.81 25.6789 18.8262 25.8767 18.925L27.5433 19.7583C27.7401 19.8571 27.8898 20.0297 27.9598 20.2384C28.0297 20.4472 28.0142 20.6751 27.9167 20.8725V20.8767ZM24.7583 13.3767C24.7094 13.2787 24.6802 13.1721 24.6725 13.0629C24.6647 12.9537 24.6786 12.844 24.7133 12.7401C24.7479 12.6363 24.8027 12.5403 24.8745 12.4576C24.9463 12.3749 25.0337 12.3072 25.1317 12.2583L26.7983 11.425C26.9961 11.3262 27.2251 11.31 27.4348 11.3801C27.6446 11.4501 27.8179 11.6005 27.9167 11.7983C28.0155 11.9961 28.0316 12.2251 27.9616 12.4348C27.8916 12.6445 27.7411 12.8179 27.5433 12.9167L25.8767 13.75C25.7787 13.799 25.6721 13.8281 25.5629 13.8359C25.4537 13.8436 25.344 13.8298 25.2401 13.7951C25.1363 13.7604 25.0403 13.7056 24.9576 13.6338C24.8749 13.562 24.8072 13.4746 24.7583 13.3767ZM24.6667 16.3333C24.6667 16.1123 24.7545 15.9004 24.9107 15.7441C25.067 15.5878 25.279 15.5 25.5 15.5H27.1667C27.3877 15.5 27.5996 15.5878 27.7559 15.7441C27.9122 15.9004 28 16.1123 28 16.3333C28 16.5543 27.9122 16.7663 27.7559 16.9226C27.5996 17.0789 27.3877 17.1667 27.1667 17.1667H25.5C25.279 17.1667 25.067 17.0789 24.9107 16.9226C24.7545 16.7663 24.6667 16.5543 24.6667 16.3333Z" fill="white" />
                                        </g>
                                    </svg>
                                </div>
                                <div className="cms-label">
                                    Electric Vehicle Consumer Code (EVCC)
                                </div>
                            </div>

                            <div className="cms-greybox">
                                <h3>EVCC resources are available to EVCC members only.</h3>
                                If you believe this is an error, please contact support or your account manager.
                            </div>
                        </div>
                    </>
                    :
                    <>
                        {bodyHtml ?
                            <>
                                {  HTMLParser(bodyHtml) }
                            </>
                            :
                            <>
                                <div className="cms-section">
                                    <div className="cms-greybox">
                                        <h3>No content has been added for this section yet.</h3>
                                    </div>
                                </div>
                            </>
                        }
                    </>
                }

            </div>
        </div>
    )
}


